import { useTheme } from '../contexts/ThemeContext'

const TankSVG = () => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const gridStroke = isDark ? 'rgba(34,198,94,0.08)' : 'rgba(34,198,94,0.14)'
  const innerBorderStroke = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)'
  const labelFill = isDark ? '#e5e7eb' : '#374151'
  const displayFill = isDark ? 'url(#displayBg)' : 'url(#displayBgLight)'

  return (
    <svg
      viewBox="0 0 300 400"
      className="w-full max-w-md h-auto"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#2dda70', stopOpacity: 0.98 }} />
          <stop offset="40%" style={{ stopColor: '#22C65E', stopOpacity: 0.95 }} />
          <stop offset="100%" style={{ stopColor: '#1a9d47', stopOpacity: 0.92 }} />
        </linearGradient>
        <linearGradient id="liquidShine" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: isDark ? 0.35 : 0.5 }} />
          <stop offset="25%" style={{ stopColor: '#ffffff', stopOpacity: isDark ? 0.08 : 0.15 }} />
          <stop offset="100%" style={{ stopColor: '#ffffff', stopOpacity: 0 }} />
        </linearGradient>
        <linearGradient id="tankBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#2dda70' }} />
          <stop offset="50%" style={{ stopColor: '#22C65E' }} />
          <stop offset="100%" style={{ stopColor: '#1a9d47' }} />
        </linearGradient>
        <filter id="tankGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="liquidGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="liquidGlowGreen" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feFlood floodColor="#22C65E" floodOpacity={isDark ? 0.4 : 0.2} result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="displayBg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#0f0f0f" />
        </linearGradient>
        <linearGradient id="displayBgLight" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f3f4f6" />
        </linearGradient>
      </defs>

      <g stroke={gridStroke} strokeWidth="0.5">
        {[50, 100, 150, 200, 250, 300, 350].map((y) => (
          <line key={`h-${y}`} x1="45" y1={y} x2="255" y2={y} />
        ))}
        {[50, 100, 150, 200, 250].map((x) => (
          <line key={`v-${x}`} x1={x} y1="45" x2={x} y2="355" />
        ))}
      </g>

      <rect
        x="50"
        y="50"
        width="200"
        height="300"
        rx="20"
        fill="none"
        stroke="url(#tankBorderGradient)"
        strokeWidth={isDark ? 4 : 3}
        filter={isDark ? 'url(#tankGlow)' : undefined}
        opacity="1"
      />
      <rect
        x="53"
        y="53"
        width="194"
        height="294"
        rx="18"
        fill={isDark ? 'none' : 'rgba(255,255,255,0.6)'}
        stroke={innerBorderStroke}
        strokeWidth="1"
      />

      <path
        fill="url(#liquidGradient)"
        filter={isDark ? 'url(#liquidGlowGreen)' : undefined}
        opacity={isDark ? 0.92 : 0.95}
      >
        <animate
          attributeName="d"
          dur="4s"
          repeatCount="indefinite"
          values="
            M53,120 C83,115 137,125 167,120 C197,115 227,125 247,120 
            L247,340 Q247,350 237,350 
            L63,350 Q53,350 53,340 Z;
            
            M53,120 C83,125 137,115 167,120 C197,125 227,115 247,120 
            L247,340 Q247,350 237,350 
            L63,350 Q53,350 53,340 Z;
            
            M53,120 C83,115 137,125 167,120 C197,115 227,125 247,120 
            L247,340 Q247,350 237,350 
            L63,350 Q53,350 53,340 Z"
        />
      </path>

      {[
        { cx: 90, cy: 300, r: 4, dur: 3, delay: 0 },
        { cx: 150, cy: 280, r: 3, dur: 2.5, delay: 0.5 },
        { cx: 190, cy: 290, r: 5, dur: 3.2, delay: 1 },
        { cx: 110, cy: 310, r: 3, dur: 2.8, delay: 1.5 },
      ].map((bubble, idx) => (
        <circle
          key={idx}
          cx={bubble.cx}
          cy={bubble.cy}
          r={bubble.r}
          fill="rgba(255,255,255,0.7)"
          filter={isDark ? 'url(#liquidGlow)' : undefined}
        >
          <animate
            attributeName="cy"
            values={`${bubble.cy};120`}
            dur={`${bubble.dur}s`}
            repeatCount="indefinite"
            begin={`${bubble.delay}s`}
          />
          <animate
            attributeName="opacity"
            values="0.7;0;0.7"
            dur={`${bubble.dur}s`}
            repeatCount="indefinite"
            begin={`${bubble.delay}s`}
          />
        </circle>
      ))}

      <path
        fill="url(#liquidShine)"
        opacity="0.9"
      >
        <animate
          attributeName="d"
          dur="4s"
          repeatCount="indefinite"
          values="
            M53,120 C83,115 137,125 167,120 C197,115 227,125 247,120 L247,155 L53,155 Z;
            M53,120 C83,125 137,115 167,120 C197,125 227,115 247,120 L247,155 L53,155 Z;
            M53,120 C83,115 137,125 167,120 C197,115 227,125 247,120 L247,155 L53,155 Z"
        />
      </path>

      {[
        { cx: 265, cy: 180, r: 3 },
        { cx: 270, cy: 220, r: 2.5 },
        { cx: 268, cy: 260, r: 3.5 },
        { cx: 272, cy: 300, r: 2 },
      ].map((dot, idx) => (
        <circle
          key={`dot-${idx}`}
          cx={dot.cx}
          cy={dot.cy}
          r={dot.r}
          fill="#22C65E"
          opacity={isDark ? 0.7 : 0.55}
        >
          <animate
            attributeName="opacity"
            values={isDark ? '0.7;0.4;0.7' : '0.55;0.3;0.55'}
            dur="2s"
            repeatCount="indefinite"
            begin={`${idx * 0.5}s`}
          />
        </circle>
      ))}

      <g transform="translate(265,150)">
        <rect
          x="-34"
          y="-20"
          width="48"
          height="36"
          fill={displayFill}
          stroke="url(#tankBorderGradient)"
          strokeWidth="2"
          rx="8"
          filter={isDark ? 'url(#liquidGlow)' : undefined}
        />
        <text
          x="-10"
          y="6"
          fill="#22C65E"
          fontFamily="monospace"
          fontSize="17"
          fontWeight="bold"
          textAnchor="middle"
        >
          85%
          <animate attributeName="opacity" values="1;0.85;1" dur="2s" repeatCount="indefinite" />
        </text>
      </g>

      {[
        { y: 100, label: '100%', isActive: true },
        { y: 200, label: '50%', isActive: false },
        { y: 300, label: '0%', isActive: true },
      ].map((indicator, idx) => (
        <g key={idx} strokeWidth="1">
          <line
            x1="30"
            y1={indicator.y}
            x2="47"
            y2={indicator.y}
            stroke="#22C65E"
            strokeWidth="2"
            opacity={isDark ? 0.85 : 0.75}
          >
            <animate
              attributeName="opacity"
              values={indicator.isActive ? '0.8;0.4;0.8' : '0.4;0.8;0.4'}
              dur="1.5s"
              repeatCount="indefinite"
            />
          </line>
          <text
            x="5"
            y={indicator.y + 4}
            fontSize="12"
            fill={labelFill}
            fontFamily="sans-serif"
            fontWeight="700"
            opacity="0.95"
          >
            {indicator.label}
          </text>
        </g>
      ))}

      <g fill="none" stroke="#22C65E" strokeWidth="2.5" opacity={isDark ? 0.9 : 0.75}>
        <circle cx="35" cy="75" r="5" fill="#22C65E" opacity="0.3" />
        <circle cx="35" cy="325" r="5" fill="#22C65E" opacity="0.3" />
        <path d="M30,75 H20 M30,325 H20" stroke="#22C65E" strokeWidth="3" opacity="1" />
      </g>

      {[0, 25, 50].map((offset, idx) => (
        <circle
          key={`sensor-${idx}`}
          cx="255"
          cy={220 + offset}
          r="6"
          fill="#22C65E"
          opacity={isDark ? 0.9 : 0.7}
        >
          <animate
            attributeName="r"
            values="6;8;6"
            dur="2s"
            repeatCount="indefinite"
            begin={`${idx * 0.7}s`}
          />
          <animate
            attributeName="opacity"
            values={isDark ? '0.9;0.5;0.9' : '0.7;0.4;0.7'}
            dur="2s"
            repeatCount="indefinite"
            begin={`${idx * 0.7}s`}
          />
        </circle>
      ))}
    </svg>
  )
}

export default TankSVG
